import { EventAttendeesService } from "../services/eventAttendees.service"
import type { Attendee } from "../models/attendee.models"
import "../styles/eventAttendees.style.scss"

/** Escape a string for safe interpolation into innerHTML (text + attributes). */
function escapeHtml(value: string): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

/** Only allow http(s) links; otherwise return empty to avoid javascript: URLs. */
function safeHref(url: string): string {
  const v = (url ?? "").trim()
  if (/^https?:\/\//i.test(v)) return v
  return ""
}

/**
 * <tbw-event-attendees data-event-slug="hosnights-april">
 *
 * Renders the checked-in attendee directory for a single event, with a live
 * client-side search and a CSV download button. Data comes from the backend
 * GET /events/:slug/attendees (auth-gated).
 */
export class EventAttendeesComponent extends HTMLElement {
  private attendees: Attendee[] = []
  private filtered: Attendee[] = []
  private slug = ""
  private query = ""

  constructor(
    private service: EventAttendeesService = window.tbwServices.eventAttendeesService
  ) {
    super()
    this.classList.add("tbw-event-attendees")
  }

  connectedCallback() {
    this.slug = this.dataset.eventSlug?.trim() ?? ""
    if (!this.slug) {
      console.warn("[tbw-event-attendees] missing data-event-slug attribute")
      return
    }
    this.renderLoading()
    void this.load()
  }

  private async load() {
    const result = await this.service.getAttendees(this.slug)
    if (result.status === "forbidden") {
      this.renderRestricted()
      return
    }
    if (result.status === "error") {
      this.renderError()
      return
    }
    this.attendees = result.attendees
    this.filtered = this.attendees
    this.render()
  }

  private applyFilter() {
    const q = this.query.toLowerCase().trim()
    if (!q) {
      this.filtered = this.attendees
      return
    }
    this.filtered = this.attendees.filter((a) =>
      `${a.name} ${a.company} ${a.jobTitle}`.toLowerCase().includes(q)
    )
  }

  private renderLoading() {
    this.innerHTML = `<div class="tbw-ea__state">Loading attendees…</div>`
  }

  private renderRestricted() {
    this.innerHTML = `
      <div class="tbw-ea__state tbw-ea__state--restricted">
        <strong>Attendee list restricted</strong>
        <p>This directory is only available to people who attended this event.</p>
      </div>`
  }

  private renderError() {
    this.innerHTML = `
      <div class="tbw-ea__state">
        Couldn't load the attendee list. Please try again later.
      </div>`
  }

  private render() {
    if (this.attendees.length === 0) {
      this.innerHTML = `<div class="tbw-ea__state">No attendees to show yet.</div>`
      return
    }

    this.innerHTML = `
      <div class="tbw-ea__header">
        <div class="tbw-ea__count">Attendees <span>(${this.attendees.length})</span></div>
        <button class="tbw-ea__download" type="button">Download CSV</button>
      </div>
      <input class="tbw-ea__search" type="search" placeholder="Search by name, company, or job title…" value="${escapeHtml(this.query)}" />
      <table class="tbw-ea__table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Job Title</th>
            <th>Company</th>
            <th class="tbw-ea__th-linkedin">LinkedIn</th>
          </tr>
        </thead>
        <tbody>${this.renderRows()}</tbody>
      </table>
    `

    const search = this.querySelector<HTMLInputElement>(".tbw-ea__search")
    search?.addEventListener("input", this.onSearch)
    // keep focus + caret after re-render
    if (this.query && search) {
      search.focus()
      search.setSelectionRange(search.value.length, search.value.length)
    }

    this.querySelector<HTMLButtonElement>(".tbw-ea__download")
      ?.addEventListener("click", this.onDownload)
  }

  private renderRows(): string {
    if (this.filtered.length === 0) {
      return `<tr><td colspan="4" class="tbw-ea__empty">No match for "${escapeHtml(this.query)}"</td></tr>`
    }
    return this.filtered
      .map((a) => {
        const avatar = safeHref(a.avatarUrl)
        const avatarHtml = avatar
          ? `<img class="tbw-ea__avatar" src="${escapeHtml(avatar)}" alt="${escapeHtml(a.name)}" loading="lazy" decoding="async" />`
          : `<span class="tbw-ea__avatar tbw-ea__avatar--placeholder">${escapeHtml(a.name.charAt(0))}</span>`
        const linkedin = safeHref(a.linkedin)
        const linkedinHtml = linkedin
          ? `<a class="tbw-ea__linkedin" href="${escapeHtml(linkedin)}" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn profile of ${escapeHtml(a.name)}">in</a>`
          : ""
        return `
          <tr>
            <td class="tbw-ea__name-cell">
              ${avatarHtml}
              <span class="tbw-ea__name">${escapeHtml(a.name)}</span>
            </td>
            <td>${escapeHtml(a.jobTitle)}</td>
            <td>${escapeHtml(a.company)}</td>
            <td class="tbw-ea__td-linkedin">${linkedinHtml}</td>
          </tr>`
      })
      .join("")
  }

  private onSearch = (e: Event) => {
    this.query = (e.target as HTMLInputElement).value
    this.applyFilter()
    // only re-render the tbody to keep the input mounted
    const tbody = this.querySelector("tbody")
    if (tbody) tbody.innerHTML = this.renderRows()
  }

  private onDownload = async () => {
    const blob = await this.service.getAttendeesCsvBlob(this.slug)
    if (!blob) return
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `${this.slug}-attendees.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  disconnectedCallback() {
    this.querySelector(".tbw-ea__search")?.removeEventListener("input", this.onSearch)
    this.querySelector(".tbw-ea__download")?.removeEventListener("click", this.onDownload)
  }
}
